import { NumberInput, TextInput } from '@core/components/Inputs';
import CurrencyTextField from '@core/components/Inputs/CurrencyTextField';
import { SelectInput } from '@core/components/Select';
import { useApiUtils } from '@core/util/apiUtils';
import {
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Switch,
  Tooltip,
} from '@material-ui/core';
import SelectCstClassificacaoTributaria from 'app/components/Select/SelectCstClassificacaoTributaria';
import SelectCstSituacaoTributaria from 'app/components/Select/SelectCstSituacaoTributaria';
import SelectNbs from 'app/components/Select/SelectNbs';
import {
  NaturezaTributacao,
  RegimeApuracaoTributaria,
  RegimeTributario,
  RegimeTributarioEspecial,
  SituacaoTributariaCofins,
  SituacaoTributariaPis,
  TipoTributacao,
} from 'app/main/empresa/modal/enums';
import empresaOnboardingApi from 'app/services/api/empresaOnboardngApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { useFormik } from 'formik';
import React, { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { OnboardingContext } from '../OnboardingContext';
import OnboardingLayout from '../OnboardingLayout';
import { useOnboardingSchemaInformacoesFiscais } from '../schemas/OnboardingSchemas';
import InformativoLateral from './InformativoLateral';

const InformacoesFiscaisOnboarding = () => {
  const [, setAtualizarEtapa] = useContext(OnboardingContext).atualizarEtapa;
  const [isTributado, setIsTributado] = useContext(OnboardingContext).isTributado;
  const [empresaAtual] = useContext(GlobalContext).empresa;
  const [, setLoading] = useContext(OnboardingContext).loading;
  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const initialValues = {
    id: empresaAtual?.empresaId,
    produtorRural: empresaAtual?.produtorRural ? 'true' : 'false',
    optanteSimples: '',
    incentivadorCultural: empresaAtual?.incentivadorCultural ? 'true' : 'false',
    naturezaTributacao: empresaAtual?.naturezaTributacao,
    regimeApuracaoTributaria: empresaAtual?.regimeApuracaoTributaria,
    tipoTributacao: empresaAtual?.tipoTributacao,
    situacaoTributariaPis: empresaAtual?.situacaoTributariaPis,
    aliquotaPis: empresaAtual?.aliquotaPis,
    situacaoTributariaCofins: empresaAtual?.situacaoTributariaCofins,
    aliquotaCofins: empresaAtual?.aliquotaCofins,
    ibsCbs: empresaAtual?.ibsCbs || false,
    contribuinteIbs: empresaAtual?.contribuinteIbs ? true : false,
    contribuinteCbs: empresaAtual?.contribuinteCbs ? true : false,
    contribuinteIs: empresaAtual?.contribuinteIs ? true : false,
    tipoIndicadorOperacao: empresaAtual?.tipoIndicadorOperacao || '',
    nbs: {
      id: empresaAtual?.nbs?.id || '',
      codigo: empresaAtual?.nbs?.codigo || '',
      descricao: empresaAtual?.nbs?.descricao || '',
    },
    cstSituacaoTributaria: {
      id: empresaAtual?.cstSituacaoTributaria?.id || '',
      codigo: empresaAtual?.cstSituacaoTributaria?.codigo || '',
      descricao: empresaAtual?.cstSituacaoTributaria?.descricao || '',
      idSituacao: empresaAtual?.cstSituacaoTributaria?.idSituacao || '',
    },
    cstClassificacaoTributaria: {
      id: empresaAtual?.cstClassificacaoTributaria?.id || '',
      codigo: empresaAtual?.cstClassificacaoTributaria?.codigo || '',
      descricao: empresaAtual?.cstClassificacaoTributaria?.descricao || '',
      cstPaiId: empresaAtual?.cstClassificacaoTributaria?.cstPaiId || '',
    },
  };

  const formik = useFormik({
    validationSchema: useOnboardingSchemaInformacoesFiscais(),
    initialValues,
    onSubmit: () => null,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
  });

  useEffect(() => {
    async function carrega() {
      const statusTributacao = await empresaOnboardingApi.findStatusTributacao();
      setIsTributado(statusTributacao.data);
    }
    carrega();
  }, [setIsTributado]);

  useEffect(() => {
    formik.resetForm(initialValues);
    if (isTributado) {
      initialValues.optanteSimples = empresaAtual.optanteSimples ? 'true' : 'false';
      initialValues.regimeTributario = empresaAtual.regimeTributario;
      initialValues.regimeTributarioEspecial = empresaAtual.regimeTributarioEspecial;
    }
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaAtual, isTributado]);

  const disabledColor = (campo, isDisabled) => {
    if (isDisabled) {
      return <span style={{ color: '#d3d3d3' }}> {campo} </span>;
    }
    return <span> {campo}</span>;
  };

  const campoSituacaoTributariaPis = () => {
    return (
      <Grid item xs={12} sm={6} md={6} lg={6}>
        <div style={{ marginBottom: '7px' }}>Situação tributária PIS</div>
        <SelectInput
          id="situacaoTributariaPis"
          label=""
          value={formik.values?.situacaoTributariaPis}
          onChange={handleChangeSituacaoTributariaPis}
          onBlur={formik.handleBlur}
          placeholder="Selecione"
          options={[
            { id: 'OPERACAO_TRIBUTAVEL_01', title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_01 },
            { id: 'OPERACAO_TRIBUTAVEL_02', title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_02 },
            { id: 'OPERACAO_TRIBUTAVEL_03', title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_03 },
            { id: 'OPERACAO_TRIBUTAVEL_04', title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_04 },
            { id: 'OPERACAO_TRIBUTAVEL_05', title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_05 },
            { id: 'OPERACAO_TRIBUTAVEL_06', title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_06 },
            { id: 'OPERACAO_TRIBUTAVEL_07', title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_07 },
            { id: 'OPERACAO_TRIBUTAVEL_08', title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_08 },
            { id: 'OPERACAO_TRIBUTAVEL_09', title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_09 },
            { id: 'OPERACAO_TRIBUTAVEL_49', title: SituacaoTributariaPis.OPERACAO_TRIBUTAVEL_49 },
          ]}
        />
      </Grid>
    );
  };

  const campoAliquotaPisPorcentagem = () => {
    return (
      <div>
        <div style={{ marginBottom: '7px' }}>
          {disabledColor('Alíquota do PIS (%)', !formik.values?.situacaoTributariaPis)}
        </div>
        <NumberInput
          id="aliquotaPis"
          name="aliquotaPis"
          label=""
          decimalScale={8}
          decimalSeparator=","
          allowNegative={false}
          isAllowed={(values) => {
            return !values.floatValue || values.floatValue <= 100;
          }}
          value={formik.values?.aliquotaPis}
          error={_errors.aliquotaPis}
          touched={!!_errors.aliquotaPis}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={!formik.values?.situacaoTributariaPis}
        />
      </div>
    );
  };

  const campoAliquotaPisMonetario = () => {
    return (
      <div>
        <div style={{ marginBottom: '7px' }}>
          {disabledColor('Alíquota (R$)', !formik.values?.situacaoTributariaPis)}
        </div>
        <CurrencyTextField
          id="aliquotaPis"
          name="aliquotaPis"
          label=""
          outputFormat="number"
          decimalCharacter=","
          digitGroupSeparator="."
          maximumValue="999999999.99"
          decimalPlaces={2}
          decimalPlacesShownOnBlur={2}
          decimalPlacesShownOnFocus={2}
          value={formik.values?.aliquotaPis}
          error={!!_errors.aliquotaPis}
          touched={!!_errors.aliquotaPis}
          helperText={_errors.aliquotaPis || ''}
          onChange={(e, value) => {
            formik.setFieldValue('aliquotaPis', value);
          }}
          onBlur={formik.handleBlur}
          variant="outlined"
          fullWidth
        />
      </div>
    );
  };

  const campoAliquotaPisST = () => {
    return (
      <div>
        <div style={{ marginBottom: '7px' }}>
          {disabledColor('Alíquota ST do PIS (%)', !formik.values?.situacaoTributariaPis)}
        </div>
        <NumberInput
          id="aliquotaPis"
          name="aliquotaPis"
          label=""
          decimalScale={8}
          decimalSeparator=","
          allowNegative={false}
          isAllowed={(values) => {
            return !values.floatValue || values.floatValue <= 100;
          }}
          value={formik.values?.aliquotaPis}
          error={_errors.aliquotaPis}
          touched={!!_errors.aliquotaPis}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={!formik.values?.situacaoTributariaPis}
        />
      </div>
    );
  };

  const campoSituacaoTributariaCofins = () => {
    return (
      <Grid item xs={12} sm={6} md={6} lg={6}>
        <div style={{ marginBottom: '7px' }}>Situação tributária COFINS</div>
        <SelectInput
          id="situacaoTributariaCofins"
          label=""
          onChange={handleChangeSituacaoTributariaCofins}
          onBlur={formik.handleBlur}
          value={formik.values?.situacaoTributariaCofins}
          placeholder="Selecione"
          options={[
            { id: 'OPERACAO_TRIBUTAVEL_01', title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_01 },
            { id: 'OPERACAO_TRIBUTAVEL_02', title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_02 },
            { id: 'OPERACAO_TRIBUTAVEL_03', title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_03 },
            { id: 'OPERACAO_TRIBUTAVEL_04', title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_04 },
            { id: 'OPERACAO_TRIBUTAVEL_05', title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_05 },
            { id: 'OPERACAO_TRIBUTAVEL_06', title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_06 },
            { id: 'OPERACAO_TRIBUTAVEL_07', title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_07 },
            { id: 'OPERACAO_TRIBUTAVEL_08', title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_08 },
            { id: 'OPERACAO_TRIBUTAVEL_09', title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_09 },
            { id: 'OPERACAO_TRIBUTAVEL_49', title: SituacaoTributariaCofins.OPERACAO_TRIBUTAVEL_49 },
          ]}
        />
      </Grid>
    );
  };

  const campoAliquotaCofinsPorcentagem = () => {
    return (
      <div>
        <div style={{ marginBottom: '7px' }}>
          {disabledColor('Alíquota do COFINS (%)', !formik.values?.situacaoTributariaCofins)}
        </div>
        <NumberInput
          id="aliquotaCofins"
          name="aliquotaCofins"
          label=""
          decimalScale={8}
          decimalSeparator=","
          allowNegative={false}
          isAllowed={(values) => {
            return !values.floatValue || values.floatValue <= 100;
          }}
          value={formik.values?.aliquotaCofins}
          error={_errors.aliquotaCofins}
          touched={!!_errors.aliquotaCofins}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={!formik.values?.situacaoTributariaCofins}
        />
      </div>
    );
  };

  const campoAliquotaCofinsMonetario = () => {
    return (
      <div>
        <div style={{ marginBottom: '7px' }}>
          {disabledColor('Alíquota (R$)', !formik.values?.situacaoTributariaCofins)}
        </div>
        <CurrencyTextField
          id="aliquotaCofins"
          name="aliquotaCofins"
          label=""
          decimalCharacter=","
          digitGroupSeparator="."
          maximumValue="999999999.99"
          decimalPlaces={2}
          decimalPlacesShownOnBlur={2}
          decimalPlacesShownOnFocus={2}
          value={formik.values?.aliquotaCofins}
          error={_errors.aliquotaCofins}
          touched={!!_errors.aliquotaCofins}
          helperText={_errors.aliquotaCofins || ''}
          onChange={(e, value) => {
            formik.setFieldValue('aliquotaCofins', value);
          }}
          onBlur={formik.handleBlur}
          variant="outlined"
          fullWidth
        />
      </div>
    );
  };

  const campoAliquotaCofinsST = () => {
    return (
      <div>
        <div style={{ marginBottom: '7px' }}>
          {disabledColor('Alíquota ST do COFINS (%)', !formik.values?.situacaoTributariaCofins)}
        </div>
        <NumberInput
          id="aliquotaCofins"
          name="aliquotaCofins"
          label=""
          decimalScale={8}
          decimalSeparator=","
          allowNegative={false}
          isAllowed={(values) => {
            return !values.floatValue || values.floatValue <= 100;
          }}
          value={formik.values?.aliquotaCofins}
          error={_errors.aliquotaCofins}
          touched={!!_errors.aliquotaCofins}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={!formik.values?.situacaoTributariaCofins}
        />
      </div>
    );
  };

  const camposAliquotas = (operacaoTributavel, isCofins) => {
    const camposVazios = [
      'OPERACAO_TRIBUTAVEL_04',
      'OPERACAO_TRIBUTAVEL_06',
      'OPERACAO_TRIBUTAVEL_07',
      'OPERACAO_TRIBUTAVEL_08',
      'OPERACAO_TRIBUTAVEL_09',
    ];

    let campoResultado = isCofins ? campoAliquotaCofinsPorcentagem() : campoAliquotaPisPorcentagem();

    if (camposVazios.includes(operacaoTributavel)) {
      campoResultado = null;
    } else if (operacaoTributavel === 'OPERACAO_TRIBUTAVEL_03') {
      campoResultado = isCofins ? campoAliquotaCofinsMonetario() : campoAliquotaPisMonetario();
    } else if (operacaoTributavel === 'OPERACAO_TRIBUTAVEL_05') {
      campoResultado = isCofins ? campoAliquotaCofinsST() : campoAliquotaPisST();
    }

    return (
      <Grid item xs={12} sm={6} md={6} lg={6}>
        {campoResultado}
      </Grid>
    );
  };

  const campos = (values) => {
    const { optanteSimples } = values;
    return optanteSimples === 'false' ? (
      <>
        {campoSituacaoTributariaPis()}
        {camposAliquotas(values?.situacaoTributariaPis, false)}
        {campoSituacaoTributariaCofins()}
        {camposAliquotas(values?.situacaoTributariaCofins, true)}
      </>
    ) : null;
  };

  const handleSubmit = async () => {
    formik.submitForm();

    if (!formik.isValid) {
      return;
    }

    setLoading(true);

    const empresaId = empresaAtual?.id || '';
    formik.values.optanteSimples = formik.values.optanteSimples === 'true';
    formik.values.incentivadorCultural = formik.values.incentivadorCultural === 'true';

    formik.values.cstSituacaoTributariaId = formik?.values?.cstSituacaoTributaria?.id;
    delete formik.values.cstSituacaoTributaria;

    formik.values.cstClassificacaoTributariaId = formik?.values?.cstClassificacaoTributaria?.id;
    delete formik.values.cstClassificacaoTributaria;

    formik.values.nbsId = formik?.values?.nbs?.id;
    delete formik.values.nbs;

    const finalRequest = { empresaRequest: formik.values };

    try {
      await empresaOnboardingApi.atualizar(empresaId, 'INFORMACOES_FISCAIS', finalRequest);
      setAtualizarEtapa(true);
    } catch (err) {
      apiUtils.handleApiError(err);
      setLoading(false);
    }
  };

  const handleChangeProdutorRural = (event) => {
    formik.handleChange(event);

    // RNE-1115
    if (event?.target?.value === 'true') {
      handleChangeOptanteSimples({
        target: {
          id: 'optanteSimples',
          value: 'false',
        },
      });

      formik.handleChange({
        target: {
          id: 'regimeTributario',
          value: 'NENHUM',
        },
      });

      formik.handleChange({
        target: {
          id: 'regimeTributarioEspecial',
          value: 'SEM_REGIME_TRIBUTARIO',
        },
      });

      formik.handleChange({
        target: {
          id: 'ibsCbs',
          value: true,
        },
      });
    }
  };

  const handleChangeOptanteSimples = (event) => {
    formik.handleChange(event);
    formik.handleChange({ target: { id: 'situacaoTributariaPis', value: '' } });
    formik.handleChange({ target: { id: 'aliquotaPis', value: '' } });
    formik.handleChange({ target: { id: 'situacaoTributariaCofins', value: '' } });
    formik.handleChange({ target: { id: 'aliquotaCofins', value: '' } });

    // RNE-626
    if (event?.target?.value === 'true') {
      formik.handleChange({
        target: {
          id: 'ibsCbs',
          value: false,
        },
      });

      formik.handleChange({
        target: {
          id: 'cstSituacaoTributaria',
          value: null,
        },
      });

      formik.handleChange({
        target: {
          id: 'cstClassificacaoTributaria',
          value: null,
        },
      });

      formik.handleChange({
        target: {
          id: 'contribuinteIbs',
          value: null,
        },
      });

      formik.handleChange({
        target: {
          id: 'contribuinteCbs',
          value: null,
        },
      });

      formik.handleChange({
        target: {
          id: 'contribuinteIs',
          value: null,
        },
      });

      formik.handleChange({
        target: {
          id: 'tipoIndicadorOperacao',
          value: null,
        },
      });
    }
  };

  const handleChangeSituacaoTributariaPis = (event) => {
    formik.handleChange(event);
    formik.handleChange({ target: { id: 'aliquotaPis', value: '' } });
  };

  const handleChangeSituacaoTributariaCofins = (event) => {
    formik.handleChange(event);
    formik.handleChange({ target: { id: 'aliquotaCofins', value: '' } });
  };

  const _errors = {
    produtorRural: (formik.submitCount > 0 || formik.touched?.produtorRural) && formik.errors?.produtorRural,
    optanteSimples: (formik.submitCount > 0 || formik.touched?.optanteSimples) && formik.errors?.optanteSimples,
    regimeTributario: (formik.submitCount > 0 || formik.touched?.regimeTributario) && formik.errors?.regimeTributario,
    regimeApuracaoTributaria:
      (formik.submitCount > 0 || formik.touched?.regimeApuracaoTributaria) && formik.errors?.regimeApuracaoTributaria,
    regimeTributarioEspecial:
      (formik.submitCount > 0 || formik.touched?.regimeTributarioEspecial) && formik.errors?.regimeTributarioEspecial,
    aliquotaPis: (formik.submitCount > 0 || formik.touched?.aliquotaPis) && formik.errors?.aliquotaPis,
    aliquotaCofins: (formik.submitCount > 0 || formik.touched?.aliquotaCofins) && formik.errors?.aliquotaCofins,
  };

  const confirmarAlteracaoIbsCbs = (isChecked) => {
    formik.handleChange({
      target: {
        id: 'ibsCbs',
        name: 'ibsCbs',
        value: isChecked,
      },
    });

    if (!isChecked) {
      formik.handleChange({
        target: {
          id: 'cstSituacaoTributaria',
          value: null,
        },
      });

      formik.handleChange({
        target: {
          id: 'cstClassificacaoTributaria',
          value: null,
        },
      });

      formik.handleChange({
        target: {
          id: 'contribuinteIbs',
          value: null,
        },
      });

      formik.handleChange({
        target: {
          id: 'contribuinteCbs',
          value: null,
        },
      });

      formik.handleChange({
        target: {
          id: 'contribuinteIs',
          value: null,
        },
      });

      formik.handleChange({
        target: {
          id: 'tipoIndicadorOperacao',
          value: null,
        },
      });
    }
  };

  const campoCstClassificacaoTributariaDesabilitado = () => {
    return (
      <Tooltip title="O campo será habilitado quando o código de situação tributária for informada" placement="top">
        <div>
          <div style={{ marginBottom: '7px' }}>Código de classificação tributária *</div>
          <TextInput id="cstClassificacaoTributaria" name="cstClassificacaoTributaria" disabled />
        </div>
      </Tooltip>
    );
  };

  const campoCstClassificacaoTributaria = () => {
    return (
      <div>
        <div style={{ marginBottom: '7px' }}>Código de classificação tributária *</div>
        <SelectCstClassificacaoTributaria
          id="cstClassificacaoTributaria"
          name="cstClassificacaoTributaria"
          value={formik.values?.cstClassificacaoTributaria}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          touched={!!_errors.cstClassificacaoTributaria}
          error={_errors.cstClassificacaoTributaria}
          disabled={!formik.values?.cstSituacaoTributaria}
          required
          idSituacao={formik.values?.cstSituacaoTributaria?.idSituacao}
        />
      </div>
    );
  };

  return (
    <OnboardingLayout
      onSubmit={handleSubmit}
      wizardStep={2}
      leftSidebarContent={
        <InformativoLateral titulo="Tributação" subTitulo="Informações fiscais" imagem="assets/images/onboarding/3.svg">
          Preenchendo todos os dados corretamente você poderá emitir as notas fiscais de serviços e produtos.
        </InformativoLateral>
      }
      content={
        <>
          <Grid container>
            <Grid item lg={2} md={2} sm={2} xs={2} />
            <Grid item lg={8} md={8} sm={8} xs={8}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <div style={{ marginBottom: '7px' }}>Produtor rural *</div>
                  <FormControl variant="outlined" fullWidth>
                    <Select
                      autoFocus
                      id="produtorRural"
                      name="produtorRural"
                      value={formik.values?.produtorRural}
                      error={_errors.produtorRural}
                      onChange={handleChangeProdutorRural}
                      onBlur={formik.handleBlur}
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected && selected.length > 0) {
                          return selected === 'true' ? 'Sim' : 'Não';
                        }
                        return <span style={{ color: '#abaaaa' }}>Selecione</span>;
                      }}
                      required
                    >
                      <MenuItem value="true"> Sim </MenuItem>
                      <MenuItem value="false"> Não </MenuItem>
                    </Select>
                    <FormHelperText style={{ color: '#f44336' }}>{_errors.produtorRural}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>Optante pelo SIMPLES Nacional *</div>
                  <FormControl variant="outlined" fullWidth>
                    <Select
                      id="optanteSimples"
                      name="optanteSimples"
                      value={formik.values?.optanteSimples}
                      error={_errors.optanteSimples}
                      onChange={handleChangeOptanteSimples}
                      onBlur={formik.handleBlur}
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected && selected.length > 0) {
                          return selected === 'true' ? 'Sim' : 'Não';
                        }
                        return <span style={{ color: '#abaaaa' }}>Selecione</span>;
                      }}
                      required
                      disabled={formik.values?.produtorRural === 'true'}
                    >
                      <MenuItem value="true"> Sim </MenuItem>
                      <MenuItem value="false"> Não </MenuItem>
                    </Select>
                    <FormHelperText style={{ color: '#f44336' }}>{_errors.optanteSimples}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>É incentivador cultural</div>
                  <FormControl variant="outlined" fullWidth>
                    <Select
                      id="incentivadorCultural"
                      name="incentivadorCultural"
                      value={formik.values?.incentivadorCultural}
                      error={_errors.incentivadorCultural}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      displayEmpty
                      renderValue={(selected) => {
                        if (selected && selected.length > 0) {
                          return selected === 'true' ? 'Sim' : 'Não';
                        }
                        return <span style={{ color: '#abaaaa' }}>Selecione</span>;
                      }}
                      required
                    >
                      <MenuItem value="true"> Sim </MenuItem>
                      <MenuItem value="false"> Não </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>Natureza da tributação</div>
                  <SelectInput
                    id="naturezaTributacao"
                    label=""
                    placeholder="Selecione"
                    value={formik.values?.naturezaTributacao}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    options={[
                      { id: 'SIMPLES_NACIONAL', title: NaturezaTributacao.SIMPLES_NACIONAL },
                      { id: 'FIXO', title: NaturezaTributacao.FIXO },
                      { id: 'DEPOSITO_EM_JUIZO', title: NaturezaTributacao.DEPOSITO_EM_JUIZO },
                      { id: 'EXIBILIDADE_DECISAO_JUDICIAL', title: NaturezaTributacao.EXIBILIDADE_DECISAO_JUDICIAL },
                      { id: 'EXIBILIDADE_PROCE_ADM', title: NaturezaTributacao.EXIBILIDADE_PROCE_ADM },
                      { id: 'ISENCAO_PARCIAL', title: NaturezaTributacao.ISENCAO_PARCIAL },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>Tipo da tributação</div>
                  <SelectInput
                    id="tipoTributacao"
                    label=""
                    placeholder="Selecione"
                    value={formik.values?.tipoTributacao}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    options={[
                      { id: 'ISENTO_ISS', title: TipoTributacao.ISENTO_ISS },
                      { id: 'IMUNE', title: TipoTributacao.IMUNE },
                      { id: 'NAO_INCI_MUNICIPIO', title: TipoTributacao.NAO_INCI_MUNICIPIO },
                      { id: 'NAO_TRIBUTAVEL', title: TipoTributacao.NAO_TRIBUTAVEL },
                      { id: 'RETIDO', title: TipoTributacao.RETIDO },
                      { id: 'TRIB_DENTRO_MUNICIPIO', title: TipoTributacao.TRIB_DENTRO_MUNICIPIO },
                      { id: 'TRIB_FORA_MUNICIPIO', title: TipoTributacao.TRIB_FORA_MUNICIPIO },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>Regime tributário *</div>
                  <SelectInput
                    id="regimeTributario"
                    label=""
                    placeholder="Selecione"
                    value={formik.values?.regimeTributario}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    touched={!!_errors.regimeTributario}
                    error={_errors.regimeTributario}
                    options={[
                      { id: 'NENHUM', title: RegimeTributario.NENHUM },
                      { id: 'SIMPLES_NACIONAL', title: RegimeTributario.SIMPLES_NACIONAL },
                      { id: 'SIMPLES_NACIONAL_EXCESSO', title: RegimeTributario.SIMPLES_NACIONAL_EXCESSO },
                      { id: 'NORMAL_LUCRO_PRESUMIDO', title: RegimeTributario.NORMAL_LUCRO_PRESUMIDO },
                      { id: 'NORMAL_LUCRO_REAL', title: RegimeTributario.NORMAL_LUCRO_REAL },
                    ]}
                    required
                    removeAsterisk
                    disabled={formik.values?.produtorRural === 'true'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>Regime tributário especial *</div>
                  <SelectInput
                    id="regimeTributarioEspecial"
                    label=""
                    placeholder="Selecione"
                    value={formik.values?.regimeTributarioEspecial}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    touched={!!_errors.regimeTributarioEspecial}
                    error={_errors.regimeTributarioEspecial}
                    options={[
                      { id: 'SEM_REGIME_TRIBUTARIO', title: RegimeTributarioEspecial.SEM_REGIME_TRIBUTARIO },
                      { id: 'MICRO_MUNICIPAL', title: RegimeTributarioEspecial.MICRO_MUNICIPAL },
                      { id: 'ESTIMATIVA', title: RegimeTributarioEspecial.ESTIMATIVA },
                      { id: 'SOCIEDADE_PROFISSIONAIS', title: RegimeTributarioEspecial.SOCIEDADE_PROFISSIONAIS },
                      { id: 'COOPERATIVA', title: RegimeTributarioEspecial.COOPERATIVA },
                      { id: 'MEI', title: RegimeTributarioEspecial.MEI },
                      { id: 'ME_EEP', title: RegimeTributarioEspecial.ME_EEP },
                    ]}
                    required
                    removeAsterisk
                    disabled={formik.values?.produtorRural === 'true'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>Regime apuração tributária *</div>
                  <SelectInput
                    id="regimeApuracaoTributaria"
                    label=""
                    placeholder="Selecione"
                    value={formik.values?.regimeApuracaoTributaria}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    touched={!!_errors.regimeApuracaoTributaria}
                    error={_errors.regimeApuracaoTributaria}
                    options={[
                      { id: 'REGIME_SN_TOTAL', title: RegimeApuracaoTributaria.REGIME_SN_TOTAL },
                      { id: 'REGIME_SN_FEDERAL_ISS_FORA', title: RegimeApuracaoTributaria.REGIME_SN_FEDERAL_ISS_FORA },
                      { id: 'REGIME_FORA_SN_TOTAL', title: RegimeApuracaoTributaria.REGIME_FORA_SN_TOTAL },
                    ]}
                  />
                </Grid>
                {campos(formik.values)}
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>Nomenclatura brasileira de serviços (NBS)</div>
                  <SelectNbs
                    id="nbs"
                    name="nbs"
                    value={formik.values?.nbs}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    touched={!!_errors.nbs}
                    error={_errors.nbs}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    label="IBS / CBS - Reforma Tributária"
                    control={
                      <Switch
                        id="ibsCbs"
                        name="ibsCbs"
                        value={formik.values?.ibsCbs}
                        checked={formik.values?.ibsCbs}
                        onChange={(event) => confirmarAlteracaoIbsCbs(event?.target?.checked)}
                        onBlur={formik.handleBlur}
                        size="small"
                        color="primary"
                      />
                    }
                    disabled={formik.values?.optanteSimples === 'true'}
                  />
                </Grid>
                {formik.values?.ibsCbs && (
                  <>
                    <Grid item xs={6}>
                      <div style={{ marginBottom: '7px' }}>Código de situação tributária (CST) *</div>
                      <SelectCstSituacaoTributaria
                        id="cstSituacaoTributaria"
                        name="cstSituacaoTributaria"
                        value={formik.values?.cstSituacaoTributaria}
                        onChange={(e) => {
                          formik.handleChange(e);
                          formik.setFieldValue('cstClassificacaoTributaria', null);
                          formik.setFieldValue('cstSituacaoTributaria.idSituacao', e.target.value?.idSituacao);
                        }}
                        onBlur={formik.handleBlur}
                        touched={!!_errors.cstSituacaoTributaria}
                        error={_errors.cstSituacaoTributaria}
                        required
                      />
                    </Grid>
                    <Grid item xs={6}>
                      {!formik.values?.cstSituacaoTributaria?.id && campoCstClassificacaoTributariaDesabilitado()}
                      {formik.values?.cstSituacaoTributaria?.id && campoCstClassificacaoTributaria()}
                    </Grid>
                    <Grid item xs={6}>
                      <div style={{ marginBottom: '7px' }}>Contribuinte IBS *</div>
                      <SelectInput
                        id="contribuinteIbs"
                        value={formik.values?.contribuinteIbs}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label=""
                        options={[
                          { id: true, title: 'Sim' },
                          { id: false, title: 'Não' },
                        ]}
                        touched={!!_errors.contribuinteIbs}
                        error={_errors.contribuinteIbs}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <div style={{ marginBottom: '7px' }}>Contribuinte CBS *</div>
                      <SelectInput
                        id="contribuinteCbs"
                        value={formik.values?.contribuinteCbs}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label=""
                        options={[
                          { id: true, title: 'Sim' },
                          { id: false, title: 'Não' },
                        ]}
                        touched={!!_errors.contribuinteCbs}
                        error={_errors.contribuinteCbs}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <div style={{ marginBottom: '7px' }}>Contribuinte do imposto seletivo (IS) *</div>
                      <SelectInput
                        id="contribuinteIs"
                        value={formik.values?.contribuinteIs}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label=""
                        options={[
                          { id: true, title: 'Sim' },
                          { id: false, title: 'Não' },
                        ]}
                        touched={!!_errors.contribuinteIs}
                        error={_errors.contribuinteIs}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <div style={{ marginBottom: '7px' }}>Indicador de operação</div>
                      <SelectInput
                        id="tipoIndicadorOperacao"
                        value={formik.values?.tipoIndicadorOperacao}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label=""
                        options={[
                          { id: 'NAO_APLICA', title: '0 - Não se aplica' },
                          { id: 'INTERNA', title: '1 - Interna' },
                          { id: 'INTERESTADUAL', title: '2 - Interestadual' },
                          { id: 'EXTERIOR', title: '3 - Exterior' },
                        ]}
                        touched={!!_errors.tipoIndicadorOperacao}
                        error={_errors.tipoIndicadorOperacao}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
            <Grid item lg={2} md={2} sm={2} xs={2} />
          </Grid>
        </>
      }
    />
  );
};

export default InformacoesFiscaisOnboarding;
